/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import {
  fetchRiskStatistics,
  fetchPendingRiskReviews,
  fetchRiskEvolution
} from 'services/hvarisk.service';
import RiskKPICard3 from 'components/cards/RiskKPICard3';
import RiskKPICard2 from 'components/cards/RiskKPICard2';
import RiskSeverityChart from 'components/charts/RiskSeverityChart';
import RiskEvolutionChart from 'components/charts/RiskEvolutionChart';
import RiskCategoryHeatmap from 'components/charts/RiskCategoryHeatmap';
import RiskRatioMiniChart from 'components/charts/RiskRatioMiniChart';
import TopRiskElementsCard from 'components/cards/TopRiskElementsCard';
import RiskFilterBar from 'components/common/RiskFilterBar';
import HVASpinner from 'components/base/HVASpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldVirus,
  faShieldDog,
  faTriangleExclamation,
  faRotate
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

const RiskDashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState({
    datefrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0],
    dateto: new Date().toISOString().split('T')[0]
  });

  // Filtros compatibles con RiskFilterBar
  const [filters, setFilters] = useState({
    serviceId: null,
    campaignId: null,
    categoryId: null,
    riskElementId: null,
    severity: null,
    dateRange: 'last30days',
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    endDate: new Date()
  });

  const [riskStats, setRiskStats] = useState({
    activeRisks: 0,
    lastWeek: 0,
    lastMonth: 0,
    older: 0,
    criticalHigh30Days: 0,
    totalAnalyzed30Days: 0
  });
  const [riskEvolution30Days, setRiskEvolution30Days] = useState<any[]>([]);
  const [analyzedEvolution30Days, setAnalyzedEvolution30Days] = useState<any[]>(
    []
  );

  const fetchDashboardData = async (currentFilters = filters) => {
    try {
      setIsLoading(true);

      // Actualizar dateRange para los componentes que lo esperan
      const newDateRange = {
        datefrom:
          currentFilters.startDate?.toISOString().split('T')[0] ||
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0],
        dateto:
          currentFilters.endDate?.toISOString().split('T')[0] ||
          new Date().toISOString().split('T')[0]
      };
      setDateRange(newDateRange);

      // Preparar parámetros de la API
      const apiParams = {
        datefrom: newDateRange.datefrom,
        dateto: newDateRange.dateto,
        serviceid: currentFilters.serviceId || undefined,
        campaignid: currentFilters.campaignId || undefined,
        categoryid: currentFilters.categoryId || undefined,
        riskelementid: currentFilters.riskElementId || undefined
      };

      // Obtener estadísticas generales
      const stats = await fetchRiskStatistics(apiParams);

      // Obtener evolución de riesgos
      const evolution = await fetchRiskEvolution({
        ...apiParams,
        interval: 'daily'
      });

      // Procesar datos para las tarjetas
      if (stats && stats.length > 0) {
        const stat = stats[0];

        // Obtener riesgos pendientes por antigüedad
        const pendingRisks = await fetchPendingRiskReviews({
          ...apiParams,
          limit: 100,
          offset: 0
        });

        // Calcular distribución por antigüedad
        const now = new Date();
        let lastWeek = 0;
        let lastMonth = 0;
        let older = 0;

        pendingRisks.risks.forEach(risk => {
          const riskDate = new Date(risk.iariskvaluedate);
          const daysDiff = Math.floor(
            (now.getTime() - riskDate.getTime()) / (1000 * 60 * 60 * 24)
          );

          if (daysDiff <= 7) {
            lastWeek++;
          } else if (daysDiff <= 30) {
            lastMonth++;
          } else {
            older++;
          }
        });

        setRiskStats({
          activeRisks: stat.pendingReviews || 0,
          lastWeek: lastWeek,
          lastMonth: lastMonth,
          older: older,
          criticalHigh30Days: stat.highSeverityRisks || 0,
          totalAnalyzed30Days: stat.totalRisks || 0
        });
      }

      // Procesar datos de evolución para gráficos
      const criticalHighData = evolution.map(day => ({
        date: day.date,
        count: (day.critical || 0) + (day.high || 0)
      }));

      const totalData = evolution.map(day => ({
        date: day.date,
        count: day.total || 0
      }));

      setRiskEvolution30Days(criticalHighData);
      setAnalyzedEvolution30Days(totalData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error loading dashboard data');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters);
    fetchDashboardData(newFilters);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchDashboardData();
    toast.info('Refreshing dashboard data...');
  };

  const formatDateRangeShort = (dateFrom: string, dateTo: string) => {
    const start = new Date(dateFrom);
    const end = new Date(dateTo);

    const formatOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    };

    const startStr = start.toLocaleDateString('en-US', formatOptions);
    const endStr = end.toLocaleDateString('en-US', formatOptions);

    // Si es el mismo año, no repetir
    if (start.getFullYear() === end.getFullYear()) {
      const startWithoutYear = start.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      return `${startWithoutYear} - ${endStr}`;
    }

    return `${startStr} - ${endStr}`;
  };

  if (isLoading && !isRefreshing) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: '400px' }}
      >
        <HVASpinner />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <Row className="gy-3 mb-4 justify-content-between">
        <Col>
          <h2 className="mb-2 text-body-emphasis">Risk Management Dashboard</h2>
          <h5 className="text-body-tertiary fw-semibold">
            Comprehensive overview of detected risks and their current status
          </h5>
        </Col>
        <Col xs="auto" className="text-end align-items-bottom">
          <Button
            variant="link"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <FontAwesomeIcon
              className="pe-2"
              icon={faRotate}
              spin={isRefreshing}
            />
            Refresh Data
          </Button>
        </Col>
      </Row>

      {/* Filtros con botones de acción */}
      <div className="mb-4 pb-3 mx-n4 px-4 mx-lg-n6 px-lg-6">
        <Row>
          <Col>
            <RiskFilterBar
              filters={filters}
              onFilterChange={handleFilterChange}
              target="dashboard"
            />
          </Col>
        </Row>
      </div>

      {/* KPIs Summary */}
      <Row className="mb-3 px-0 gy-3">
        <h3 className="mb-2">Analysis and Detection</h3>
        <Col xs={12} sm={12} md={6} lg={6} xxl={3} className="px-3 pe-0">
          <RiskKPICard2
            icon={faShieldDog}
            color="primary"
            label={formatDateRangeShort(dateRange.datefrom, dateRange.dateto)}
            title="Analyzed"
            value={riskStats.totalAnalyzed30Days}
            data={analyzedEvolution30Days}
          />
        </Col>
        <Col
          xs={12}
          sm={12}
          md={6}
          lg={6}
          xxl={3}
          className="px-3  pe-sm-0 pe-md-3 pe-xxl-0"
        >
          <RiskKPICard2
            icon={faShieldVirus}
            color="primary"
            label={formatDateRangeShort(dateRange.datefrom, dateRange.dateto)}
            title="Detected"
            value={riskStats.criticalHigh30Days}
            data={riskEvolution30Days}
          />
        </Col>
        <Col xs={12} sm={12} md={6} lg={6} xxl={3} className="px-3 pe-0">
          <RiskKPICard3
            icon={faTriangleExclamation}
            color="primary"
            label="Pending Review"
            label2="Detected last week: "
            label3="Detected last month: "
            label4="Older detections: "
            value={riskStats.activeRisks}
            value2={riskStats.lastWeek}
            value3={riskStats.lastMonth}
            value4={riskStats.older}
            title="Active Risks"
          />
        </Col>
        <Col
          xs={12}
          sm={12}
          md={6}
          lg={6}
          xxl={3}
          className="px-3 pe-sm-0 pe-md-3"
        >
          <RiskRatioMiniChart
            dateRange={dateRange}
            campaignId={filters.campaignId || undefined}
          />
        </Col>
      </Row>

      {/* Charts Section */}
      <Row className="mb-6 px-0 pt-3 gy-3">
        <Col xs={12} md={8}>
          <div>
            <RiskEvolutionChart
              title={formatDateRangeShort(dateRange.datefrom, dateRange.dateto)}
              dateRange={dateRange}
              campaignId={filters.campaignId || undefined}
              categoryId={filters.categoryId || undefined}
            />
          </div>
        </Col>
        <Col xs={12} md={4}>
          <RiskSeverityChart
            dateRange={dateRange}
            campaignId={filters.campaignId || undefined}
            categoryId={filters.categoryId || undefined}
            serviceId={filters.serviceId || undefined}
          />
        </Col>
      </Row>

      {/* Risk Element Analysis Section */}
      <Row className="mb-3 px-0 gy-3">
        <h3 className="mb-2">Risk Elements</h3>
        <Col>
          <TopRiskElementsCard
            dateRange={dateRange}
            campaignId={filters.campaignId || undefined}
            categoryId={filters.categoryId || undefined}
            serviceId={filters.serviceId || undefined}
          />
        </Col>
      </Row>

      {/* Analysis Comparison Section */}
      <Row className="mb-3 px-0 gy-3">
        <Col>
          <RiskCategoryHeatmap
            dateRange={dateRange}
            campaignId={filters.campaignId || undefined}
            categoryId={filters.categoryId || undefined}
            serviceId={filters.serviceId || undefined}
          />
        </Col>
      </Row>
    </>
  );
};

export default RiskDashboard;
